import { useMemo } from 'react';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';

import { Listbox } from './listbox';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Wall-clock time selection. Emits `HH:mm` in 24-hour form regardless of how it is
 * displayed, so the value is unambiguous while the presentation follows the locale.
 */

export interface TimePickerProps {
  /** `HH:mm`, 24-hour, or null. */
  value: string | null;
  onChange: (time: string | null) => void;
  /** Granularity of the minute list. 5 suits service times; 1 suits a log entry. */
  minuteStep?: number;
  min?: string;
  max?: string;
  disabled?: boolean;
  invalid?: boolean;
  hourLabel?: string;
  minuteLabel?: string;
  meridiemLabel?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
}

export const TimePicker = ({
  value,
  onChange,
  minuteStep = 5,
  min,
  max,
  disabled = false,
  invalid = false,
  hourLabel,
  minuteLabel,
  meridiemLabel,
  popoverPortal = 'body',
  className,
}: TimePickerProps) => {
  const locale = usePreferences((state) => state.locale);
  const uses12Hour = useMemo(() => is12HourLocale(locale), [locale]);

  const [hour, minute] = split(value);

  const hourOptions = useMemo(() => {
    if (!uses12Hour) {
      return range(0, 23).map((value24) => ({
        value: value24,
        label: String(value24).padStart(2, '0'),
      }));
    }
    // 12 first, then 1–11: the order a clock face reads, not the order the numbers sort.
    return [12, ...range(1, 11)].map((value12) => ({
      value: value12,
      label: String(value12),
    }));
  }, [uses12Hour]);

  const minuteOptions = useMemo(
    () =>
      range(0, 59)
        .filter((value60) => value60 % minuteStep === 0)
        .map((value60) => ({ value: value60, label: String(value60).padStart(2, '0') })),
    [minuteStep],
  );

  const inRange = (candidate: string): boolean =>
    (min === undefined || candidate >= min) && (max === undefined || candidate <= max);

  const emit = (nextHour: number | null, nextMinute: number | null) => {
    if (nextHour === null || nextMinute === null) {
      onChange(null);
      return;
    }
    const candidate = `${String(nextHour).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}`;
    if (inRange(candidate)) onChange(candidate);
  };

  const displayHour =
    hour === null ? null : uses12Hour ? (hour % 12 === 0 ? 12 : hour % 12) : hour;
  const isPm = hour !== null && hour >= 12;

  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-1.5', className)}>
      <Listbox<number>
        value={displayHour}
        onChange={(next) => {
          if (next === null) return;
          const hour24 = uses12Hour ? to24Hour(next, isPm) : next;
          emit(hour24, minute ?? 0);
        }}
        options={hourOptions}
        disabled={disabled}
        invalid={invalid}
        size="sm"
        className={uses12Hour ? 'w-[4.5rem] shrink-0' : 'w-[5rem] shrink-0'}
        popoverPortal={popoverPortal}
        aria-label={hourLabel}
      />

      <span aria-hidden className="shrink-0 text-fg-tertiary">
        :
      </span>

      <Listbox<number>
        value={minute}
        onChange={(next) => {
          if (next === null) return;
          emit(hour ?? 0, next);
        }}
        options={minuteOptions}
        disabled={disabled}
        invalid={invalid}
        size="sm"
        className={uses12Hour ? 'w-[4.5rem] shrink-0' : 'w-[5rem] shrink-0'}
        popoverPortal={popoverPortal}
        aria-label={minuteLabel}
      />

      {uses12Hour ? (
        <Listbox<'am' | 'pm'>
          value={isPm ? 'pm' : 'am'}
          onChange={(next) => {
            if (!next || hour === null) return;
            emit(to24Hour(displayHour ?? 12, next === 'pm'), minute ?? 0);
          }}
          options={[
            { value: 'am', label: meridiemText(locale, 'am') },
            { value: 'pm', label: meridiemText(locale, 'pm') },
          ]}
          disabled={disabled || hour === null}
          size="sm"
          className="w-[5.5rem] shrink-0"
          popoverPortal={popoverPortal}
          aria-label={meridiemLabel}
        />
      ) : null}
    </div>
  );
};

const split = (value: string | null): [number | null, number | null] => {
  if (!value) return [null, null];
  const [hour = '', minute = ''] = value.split(':');
  const parsedHour = Number.parseInt(hour, 10);
  const parsedMinute = Number.parseInt(minute, 10);
  return [
    Number.isFinite(parsedHour) ? parsedHour : null,
    Number.isFinite(parsedMinute) ? parsedMinute : null,
  ];
};

const to24Hour = (hour12: number, isPm: boolean): number => {
  const base = hour12 % 12;
  return isPm ? base + 12 : base;
};

const range = (from: number, to: number): number[] =>
  Array.from({ length: to - from + 1 }, (_, index) => from + index);

const is12HourLocale = (locale: string): boolean => {
  try {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions()
      .hourCycle?.startsWith('h1') ?? false;
  } catch {
    return false;
  }
};

/** "AM" / "PM" as the locale writes them, rather than two hardcoded literals. */
const meridiemText = (locale: string, which: 'am' | 'pm'): string => {
  try {
    const at = new Date(Date.UTC(2020, 0, 1, which === 'am' ? 9 : 21));
    const parts = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      hour12: true,
      timeZone: 'UTC',
    }).formatToParts(at);
    return parts.find((part) => part.type === 'dayPeriod')?.value ?? which.toUpperCase();
  } catch {
    return which.toUpperCase();
  }
};
